/**
 * /approvals/analytics — dedicated approval analytics dashboard.
 *
 * Full-page version of the collapsible AnalyticsPanel in _auth.approvals.tsx.
 * Shows approval metrics with configurable lookback window (7/30/90/180/365 days),
 * stat cards, bottleneck approvers table, and state breakdown.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CircularProgress, Chip } from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

import { analyticsQueryOptions } from "@/lib/queries";

export const Route = createFileRoute("/_auth/approvals/analytics")({
  component: ApprovalAnalyticsPage,
});

// ──────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────
function fmtHours(h: number | null | undefined): string {
  if (h == null) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${h.toFixed(1)}h`;
  const days = h / 24;
  return `${days.toFixed(1)}d`;
}

function fmtPct(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

const PERIOD_OPTIONS = [
  { label: "7 days", value: 7 },
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
  { label: "180 days", value: 180 },
  { label: "1 year", value: 365 },
];

// ──────────────────────────────────────────────────────────────
// Stat card
// ──────────────────────────────────────────────────────────────
function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string | number;
  sub?: string;
  color?: "blue" | "green" | "amber" | "red" | "slate";
}) {
  const colorMap = {
    blue: "bg-blue-50 dark:bg-blue-950",
    green: "bg-green-50 dark:bg-green-950",
    amber: "bg-amber-50 dark:bg-amber-950",
    red: "bg-red-50 dark:bg-red-950",
    slate: "bg-slate-50 dark:bg-gray-950",
  };
  const bg = colorMap[color ?? "slate"];

  return (
    <div className={`${bg} rounded-lg p-4 border border-transparent`}>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{label}</p>
      <p className="text-2xl font-semibold text-slate-800 dark:text-slate-100">
        {value}
      </p>
      {sub && (
        <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
          {sub}
        </p>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────
function ApprovalAnalyticsPage() {
  const [days, setDays] = useState(90);
  const q = useQuery(analyticsQueryOptions(days));
  const d = q.data;

  const approvedCount = d?.approved_count ?? 0;
  const rejectedCount = d?.rejected_count ?? 0;
  const pendingCount = d?.pending_count ?? 0;
  const total = d?.total_requests ?? 0;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden bg-slate-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <BarChartIcon sx={{ fontSize: 22, color: "#3b82f6" }} />
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Approval Analytics
          </h1>
          {q.isFetching && <CircularProgress size={14} />}
        </div>
        <p className="text-xs text-slate-500 mt-1 dark:text-slate-400">
          Approval workflow performance metrics
        </p>

        {/* Period selector */}
        <div className="mt-3 flex items-center gap-1">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setDays(opt.value)}
              className={`px-3 py-1 rounded-full text-xs transition ${
                days === opt.value
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-gray-800 dark:text-slate-300 dark:hover:bg-gray-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 space-y-6">
        {q.isError && (
          <div className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3 border border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800">
            Failed to load analytics. {(q.error as Error)?.message}
          </div>
        )}

        {/* Stats grid */}
        {d && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard
              label="Total requests"
              value={total}
              sub={`Last ${d.period_days} days`}
              color="blue"
            />
            <StatCard
              label="Pending"
              value={pendingCount}
              sub="Awaiting action"
              color="amber"
            />
            <StatCard
              label="Approval rate"
              value={fmtPct(d.approval_rate)}
              sub={`${approvedCount} approved`}
              color="green"
            />
            <StatCard
              label="Avg latency"
              value={fmtHours(d.avg_approval_latency_hours)}
              sub="From request to decision"
              color="slate"
            />
          </div>
        )}

        {/* State breakdown */}
        {d && total > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm dark:bg-gray-900 dark:border-gray-800">
            <div className="px-5 py-3 border-b border-slate-100 dark:border-gray-800">
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                State breakdown
              </h2>
            </div>
            <div className="px-5 py-4 space-y-3">
              {[
                {
                  label: "Approved",
                  count: approvedCount,
                  color: "success" as const,
                },
                {
                  label: "Rejected",
                  count: rejectedCount,
                  color: "error" as const,
                },
                {
                  label: "Pending",
                  count: pendingCount,
                  color: "warning" as const,
                },
                {
                  label: "Other",
                  count: Math.max(
                    0,
                    total - approvedCount - rejectedCount - pendingCount,
                  ),
                  color: "default" as const,
                },
              ]
                .filter((row) => row.count > 0)
                .map((row) => {
                  const pct = total > 0 ? (row.count / total) * 100 : 0;
                  return (
                    <div key={row.label} className="flex items-center gap-3">
                      <div className="w-24 flex-shrink-0">
                        <Chip
                          label={row.label}
                          size="small"
                          color={row.color}
                          variant="outlined"
                          sx={{ fontSize: 11, height: 20, width: "100%" }}
                        />
                      </div>
                      <div className="flex-1 bg-slate-100 rounded-full h-2 dark:bg-gray-800">
                        <div
                          className={`h-2 rounded-full ${
                            row.color === "success"
                              ? "bg-emerald-500"
                              : row.color === "error"
                                ? "bg-red-500"
                                : row.color === "warning"
                                  ? "bg-amber-400"
                                  : "bg-slate-400"
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono text-slate-600 dark:text-slate-300 w-16 text-right">
                        {row.count} ({pct.toFixed(0)}%)
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Bottleneck approvers */}
        {d && d.bottleneck_approvers.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm dark:bg-gray-900 dark:border-gray-800">
            <div className="px-5 py-3 border-b border-slate-100 dark:border-gray-800">
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                Slowest approvers
              </h2>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                Approvers with the longest average response time
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 dark:bg-gray-950 dark:border-gray-800">
                    <th className="text-left px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">
                      Approver
                    </th>
                    <th className="text-right px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">
                      Avg response time
                    </th>
                    <th className="text-right px-5 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide dark:text-slate-400">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                  {d.bottleneck_approvers.map((b, idx) => (
                    <tr
                      key={b.approver_id}
                      className="hover:bg-slate-50 dark:hover:bg-gray-800"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400 dark:text-slate-500 w-5">
                            #{idx + 1}
                          </span>
                          <PersonOutlineIcon
                            sx={{ fontSize: 14, color: "#94a3b8" }}
                          />
                          <span className="font-mono text-xs text-slate-700 dark:text-slate-300">
                            {b.approver_id.slice(0, 8)}…
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <span
                          className={`font-mono text-sm font-semibold ${
                            b.avg_response_hours > 48
                              ? "text-red-600 dark:text-red-400"
                              : b.avg_response_hours > 24
                                ? "text-amber-600 dark:text-amber-400"
                                : "text-slate-700 dark:text-slate-300"
                          }`}
                        >
                          {fmtHours(b.avg_response_hours)}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right text-sm text-slate-600 dark:text-slate-300">
                        {b.action_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty state */}
        {d && total === 0 && (
          <div className="text-center text-slate-400 text-sm py-16 dark:text-slate-500">
            <BarChartIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
            <p>No approval requests in the last {d.period_days} days.</p>
          </div>
        )}

        {!d && !q.isFetching && !q.isError && (
          <div className="text-center text-slate-400 text-sm py-16 dark:text-slate-500">
            <CircularProgress size={24} />
          </div>
        )}
      </div>
    </div>
  );
}
